import type { ExternalUserAccessControlInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-access-control.dto';
import type { ExternalUserFormInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-form.dto';
import type { PersonalAccountMenuItemFindInterface } from '@zaoblako/shared/modules/security/dtos/personal-account/menu-item/personal-account-menu-item-find.dto';

import { PersonalAccountMenuService } from '@zaoblako/web/core/services/lk/personal-account-menu.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ChangeDetectorRef } from '@angular/core';

interface AccessRightItemInterface {
  visible?: boolean;
  key?: keyof ExternalUserAccessControlInterface;
  label: string;
  hint?: string;
  group?: boolean;
  children?: AccessRightItemInterface[];
}

const DEFAULT_ACCESS_CONTROL: ExternalUserAccessControlInterface = {
  admin: false,
  externalUser: false,
  hdTask: false,
  cctvTransitTask: false,
  cctvResidentCar: false,
  pacsPassageTask: false,
  pacsPassageOrder: false
};

@Component({
  selector: 'crm-external-user-permissions-widget',
  standalone: true,
  imports: [CommonModule, CardModule, InputSwitchModule, FormsModule, SpinnerComponent, MessagesModule],
  templateUrl: './external-user-permissions-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserPermissionsWidgetComponent {
  @Input() public loading = false;
  @Input() public user: ExternalUserFormInterface;
  @Input() public currentUserHasEditPermission = true;
  @Input() public styleClass: string;

  @Output() public accessChange = new EventEmitter<ExternalUserFormInterface>();

  public constructor(
    private readonly personalAccountMenuService: PersonalAccountMenuService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public get userAccessControl(): ExternalUserAccessControlInterface {
    if (!this.user) {
      return { ...DEFAULT_ACCESS_CONTROL };
    }
    if (!this.user.accessControl) {
      this.user.accessControl = { ...DEFAULT_ACCESS_CONTROL };
    }
    return this.user.accessControl;
  }

  public get isAdmin(): boolean {
    return this.userAccessControl?.admin;
  }

  public accessRights: AccessRightItemInterface[] = [];

  public ngOnInit(): void {
    this.personalAccountMenuService
      .getMenu()
      .subscribe({
        next: ({ items }) => {
          this.buildMenu(items);
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.buildMenu({});
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public onAccessChange(user: ExternalUserFormInterface) {
    if (user) {
      this.accessChange.emit(user);
    }
  }

  private buildMenu(settings: Record<string, PersonalAccountMenuItemFindInterface>) {
    this.accessRights = [
      {
        key: 'externalUser',
        label: 'Сотрудники',
        visible: settings?.EXTERNAL_USER?.enabled,
        hint:
          'Создание и редактирование профилей, блокировка учетной записи, ' +
          'настройка прав для всех сотрудников, а еще выдача доступа в Личный кабинет'
      },
      {
        key: 'hdTask',
        label: 'Заявки',
        visible: settings?.HD_TASK?.enabled,
        hint: 'Создание заявки и редактирование ее реквизитов'
      },
      {
        label: 'Паркинг',
        group: true,
        visible: settings?.PARKING?.enabled,
        children: [
          {
            key: 'cctvTransitTask',
            label: 'Гостевой проезд',
            visible: settings?.PARKING?.children?.TRANSIT_TASK?.enabled,
            hint: 'Создание заявки на гостевой проезд и редактирование ее реквизитов в статусе "Новая"'
          },
          {
            key: 'cctvResidentCar',
            label: 'Постоянные ТС',
            visible: settings?.PARKING?.children?.RESIDENT_CAR?.enabled,
            hint: 'Создание, редактирование и удаление постоянных ТС'
          }
        ]
      },
      {
        label: 'Пропуска',
        group: true,
        visible: settings?.PACS?.enabled,
        children: [
          {
            key: 'pacsPassageTask',
            label: 'Приглашение гостя',
            visible: settings?.PACS?.children?.PASSAGE_TASK?.enabled,
            hint: 'Создание заявки по приглашению гостя и редактирование ее реквизитов в статусе "Новая", а еще повтор заявки'
          },
          {
            key: 'pacsPassageOrder',
            label: 'Заказ пропусков',
            visible: settings?.PACS?.children?.PASSAGE_ORDER?.enabled,
            hint: 'Создание заявки по оформлению постоянных пропусков для сотрудников и редактирование ее реквизитов в статусе "Новая"'
          }
        ]
      }
    ];
  }
}
